import { useState, useEffect } from "react";
import { api } from "@/api/client";
import { Save, Building, FileText, Box, Image, MapPin, Trash2, Package } from "lucide-react";
import SecretsSection from "@/components/SecretsSection";
import StoredFile from "@/components/StoredFile";
import UsersSection from "@/components/UsersSection";
import AboutSection from "@/components/AboutSection";
import EmailSection from "@/components/EmailSection";

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    company_name: "",
    permit_number: "",
    crid_number: "",
    mailer_id: "",
    phone_number: "",
    email: "",
    permit_type: "Permit Imprint",
    street_address: "",
    city: "",
    state: "",
    zip: "",
    pvds_template: "", // Added for new functionality
    facing_slip_template: "", // Added for new functionality
    label_template: "", // Added for new functionality
    signature_png: "", // Added for signature
    postage_statement_addresses: [], // Added for saved addresses
    handling_fee_percent: 45,
    handling_fee_amount: 1.25,
  });
  const [boxSizes, setBoxSizes] = useState([]);
  const [paperStocks, setPaperStocks] = useState([]);
  const [postOffices, setPostOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("company");
  const [uploadingTemplate, setUploadingTemplate] = useState(null); // New state

  // New state for box modal
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [editingBox, setEditingBox] = useState(null); // Stores the box object being edited, or null for new
  const [savingBox, setSavingBox] = useState(false);
  const [boxForm, setBoxForm] = useState({
    name: "",
    type: "Box",
    width: "", // Changed to string for input fields
    length: "", // Changed to string for input fields
    height: "", // Changed to string for input fields
    box_empty_weight: "", // Changed to string for input fields
    wall_thickness_in: "0.25",
    active: true // Added for new functionality
  });

  const [editingPaper, setEditingPaper] = useState(null);
  const [savingPaper, setSavingPaper] = useState(false);
  const [paperForm, setPaperForm] = useState({
    paper_name: "",
    width: "",
    length: "",
    thickness: "",
    weight_lbs_per_100: ""
  });

  useEffect(() => {
    loadSettings();
    loadBoxSizes();
    loadPaperStocks();
    loadPostOffices();
  }, []);

  // Settings is a single row now, not a one-element list, so there is no
  // "create if missing" branch: the row is seeded by the migration.
  const loadSettings = async () => {
    try {
      setSettings(await api.settings.get());
    } catch (err) {
      console.error(err);
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const loadBoxSizes = async () => {
    try {
      const data = await api.entities.BoxSize.list();
      if (data && Array.isArray(data)) {
        // Sort boxes: active first, then by name
        const sortedData = data.sort((a, b) => {
          if (a.active === b.active) {
            return a.name.localeCompare(b.name);
          }
          return a.active === false ? 1 : -1;
        });
        setBoxSizes(sortedData);
      } else {
        setBoxSizes([]);
      }
    } catch (err) {
      console.error('Failed to load box sizes:', err);
      setError("Failed to load box sizes: " + err.message);
    }
  };

  const loadPaperStocks = async () => {
    try {
      const data = await api.entities.PaperStock.list();
      const sorted = (data || []).sort((a, b) => a.paper_name.localeCompare(b.paper_name, undefined, { numeric: true, sensitivity: 'base' }));
      setPaperStocks(sorted);
    } catch (err) {
      console.error('Failed to load paper stocks:', err);
      setError("Failed to load paper stocks: " + err.message);
    }
  };

  const loadPostOffices = async () => {
    try {
      const data = await api.entities.PostOffice.list();
      setPostOffices(data || []);
    } catch (err) {
      console.error('Failed to load post offices:', err);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings.company_name || !settings.permit_number) {
      setError("Company Name and Permit Number are required");
      return;
    }

    // A number input hands back text. Left empty it would reach the database
    // as '' and fail with a type error nobody could act on, so the fee is
    // checked here and sent as a number.
    const rawFee = settings.handling_fee_percent;
    const handlingFee = Number(rawFee);
    if (rawFee === "" || rawFee == null || !Number.isFinite(handlingFee) || handlingFee < 0 || handlingFee > 999.99) {
      setError("Default Handling Fee must be a percentage from 0 to 999.99.");
      return;
    }

    // The flat fee is checked the same way and for the same reason. It is a
    // separate value from the percentage, not a second rendering of it - the
    // billing export writes each into its own column and they are added, so a
    // percentage typed in here would be charged as dollars.
    const rawFeeAmount = settings.handling_fee_amount;
    const handlingFeeAmount = Number(rawFeeAmount);
    if (
      rawFeeAmount === "" ||
      rawFeeAmount == null ||
      !Number.isFinite(handlingFeeAmount) ||
      handlingFeeAmount < 0 ||
      handlingFeeAmount > 99999999.99
    ) {
      setError("Default Handling Fee Amount must be an amount in dollars, 0 or more.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      setSettings(await api.settings.update({
        ...settings,
        handling_fee_percent: handlingFee,
        handling_fee_amount: handlingFeeAmount,
      }));
      setSuccess("Settings saved.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBox = async () => {
    if (!boxForm.name || !boxForm.type || !boxForm.width || !boxForm.length || !boxForm.height || !boxForm.box_empty_weight) {
      setError("Please fill in all box/tray fields.");
      return;
    }

    const parsedWidth = parseFloat(boxForm.width);
    const parsedLength = parseFloat(boxForm.length);
    const parsedHeight = parseFloat(boxForm.height);
    const parsedWeight = parseFloat(boxForm.box_empty_weight);

    if (isNaN(parsedWidth) || isNaN(parsedLength) || isNaN(parsedHeight) || isNaN(parsedWeight) ||
      parsedWidth <= 0 || parsedLength <= 0 || parsedHeight <= 0 || parsedWeight < 0) {
      setError("Dimensions must be positive numbers and weight must be non-negative.");
      return;
    }

    setSavingBox(true);
    setError("");

    try {
      const data = {
        ...boxForm,
        width: parsedWidth,
        length: parsedLength,
        height: parsedHeight,
        box_empty_weight: parsedWeight,
        // Interior dimensions decide what fits; the carrier charges for the
        // exterior, which is the interior plus this wall on each side.
        // A blank field is not zero: a box with no walls does not exist, and
        // treating it as zero quietly under-declares the parcel.
        wall_thickness_in: Number.isFinite(parseFloat(boxForm.wall_thickness_in))
          ? parseFloat(boxForm.wall_thickness_in)
          : 0.25,
      };

      if (editingBox && editingBox.id) {
        await api.entities.BoxSize.update(editingBox.id, data);
        setSuccess("Box/Tray updated successfully!");
      } else {
        await api.entities.BoxSize.create(data);
        setSuccess("Box/Tray added successfully!");
      }

      await loadBoxSizes();
      setShowBoxModal(false);
      setEditingBox(null);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to save box/tray");
    } finally {
      setSavingBox(false);
    }
  };

  const handleAddPaperStock = () => {
    setEditingPaper("new");
    setPaperForm({
      paper_name: "",
      width: 0,
      length: 0,
      thickness: 0,
      weight_lbs_per_100: 0
    });
    setError("");
  };

  const handleEditPaper = (paper) => {
    setEditingPaper(paper.id);
    setPaperForm({
      paper_name: paper.paper_name,
      width: paper.width,
      length: paper.length,
      thickness: paper.thickness,
      weight_lbs_per_100: paper.weight_lbs_per_100 || 0
    });
    setError("");
  };

  const handleSavePaper = async () => {
    if (!paperForm.paper_name || paperForm.width === undefined || paperForm.width === "" || paperForm.length === undefined || paperForm.length === "" || paperForm.thickness === undefined || paperForm.thickness === "" || paperForm.weight_lbs_per_100 === undefined || paperForm.weight_lbs_per_100 === "") {
      setError("Please fill in all paper stock fields");
      return;
    }

    const width = parseFloat(paperForm.width);
    const length = parseFloat(paperForm.length);
    const thickness = parseFloat(paperForm.thickness);
    const weight = parseFloat(paperForm.weight_lbs_per_100);

    if (isNaN(width) || isNaN(length) || isNaN(thickness) || isNaN(weight) || width <= 0 || length <= 0 || thickness <= 0 || weight <= 0) {
      setError("All dimensions and weight must be valid numbers greater than 0");
      return;
    }

    setSavingPaper(true);
    setError("");

    try {
      const dataToSave = {
        paper_name: paperForm.paper_name,
        width,
        length,
        thickness,
        // Named for what it measures. The old column was just `weight`, next to
        // a per-single-piece `piece_weight` on the mailing — a factor of 100
        // apart, both feeding the PVDS, with nothing to tell them apart.
        weight_lbs_per_100: weight
      };

      if (editingPaper === "new") {
        await api.entities.PaperStock.create(dataToSave);
      } else {
        await api.entities.PaperStock.update(editingPaper, dataToSave);
      }
      await loadPaperStocks();
      setEditingPaper(null);
      setSuccess(`Paper stock ${editingPaper === "new" ? "added" : "updated"} successfully!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError(`Failed to ${editingPaper === "new" ? "add" : "update"} paper stock: ${err.message}`);
    } finally {
      setSavingPaper(false);
    }
  };

  const handleDeletePaperStock = async (id) => {
    if (!confirm("Are you sure you want to delete this paper stock?")) {
      return;
    }

    try {
      await api.entities.PaperStock.delete(id);
      await loadPaperStocks();
      setSuccess("Paper stock deleted successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError("Failed to delete paper stock");
    }
  };

  const handleAddPostOffice = async () => {
    try {
      await api.entities.PostOffice.create({
        name: "New Post Office",
        address_line1: "",
        city: "",
        state: "",
        zip: ""
      });
      loadPostOffices();
    } catch (err) {
      console.error(err);
      setError("Failed to add post office");
    }
  };

  const handleDeletePostOffice = async (id) => {
    try {
      await api.entities.PostOffice.delete(id);
      loadPostOffices();
    } catch (err) {
      console.error(err);
      setError("Failed to delete post office");
    }
  };

  /**
   * Upload one of the blank PDF forms the generators overlay onto.
   *
   * The API stores the file in a private bucket and returns an object path.
   * That path is what goes on the settings row — Base44 stored a permanent
   * public URL, so every template stayed readable by anyone with the link.
   */
  const handleUploadTemplate = async (templateType, file) => {
    if (!file || !file.name) return;

    setUploadingTemplate(templateType);
    setError("");

    try {
      const { path } = await api.upload('company-template', file);
      const field = `${templateType}_template`;

      const updated = await api.settings.update({ [field]: path });
      setSettings(updated);

      setSuccess(`${templateType.replace(/_/g, ' ')} template uploaded.`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError(
        err.isForbidden
          ? 'Only an administrator can change the shop templates.'
          : err.message || `Failed to upload the ${templateType} template.`
      );
      setTimeout(() => setError(""), 6000);
    } finally {
      setUploadingTemplate(null);
    }
  };

  const handleUploadSignature = async (file) => {
    if (!file || !file.name) return;

    setUploadingTemplate('signature');
    setError("");

    try {
      const { path } = await api.upload('signature', file);
      const updated = await api.settings.update({ signature_png: path });
      setSettings(updated);

      setSuccess("Signature uploaded.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError(
        err.isForbidden
          ? 'Only an administrator can change the signature.'
          : err.message || 'Failed to upload the signature.'
      );
      setTimeout(() => setError(""), 6000);
    } finally {
      setUploadingTemplate(null);
    }
  };

  const handleDeleteTemplate = async (templateType) => {
    try {
      if (!settings.id) {
        setError("Settings not found, cannot delete template.");
        return;
      }
      const updatedSettings = {
        ...settings,
        [`${templateType}_template`]: ""
      };

      await api.entities.Settings.update(settings.id, updatedSettings);
      setSettings(updatedSettings);
      setSuccess(`${templateType.toUpperCase()} template removed successfully!`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError(`Failed to remove ${templateType} template`);
    }
  };

  const handleDeleteSignature = async () => {
    try {
      if (!settings.id) {
        setError("Settings not found, cannot delete signature.");
        return;
      }
      const updatedSettings = {
        ...settings,
        signature_png: ""
      };

      await api.entities.Settings.update(settings.id, updatedSettings);
      setSettings(updatedSettings);
      setSuccess("Signature removed successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError("Failed to remove signature");
    }
  };

  const handleDeleteSavedAddress = async (addressIndex) => {
    if (!confirm("Delete this saved address?")) return;

    try {
      const updatedAddresses = settings.postage_statement_addresses.filter((_, idx) => idx !== addressIndex);
      const updatedSettings = {
        ...settings,
        postage_statement_addresses: updatedAddresses
      };

      if (settings.id) {
        await api.entities.Settings.update(settings.id, { postage_statement_addresses: updatedAddresses });
        setSettings(updatedSettings);
        setSuccess("Address deleted!");
        setTimeout(() => setSuccess(""), 3000);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to delete address");
      setTimeout(() => setError(""), 3000);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="header">
        <div>
          <div className="header-title">Settings</div>
          <div className="header-subtitle">Configure your EDDM settings</div>
        </div>
      </header>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Tabs */}
      <div style={{
        display: "flex",
        gap: "0.5rem",
        marginBottom: "1.5rem",
        borderBottom: "2px solid #e2e8f0",
        paddingBottom: "0.5rem"
      }}>
        <button
          className={`btn btn-sm ${activeTab === "company" ? "btn-primary" : "btn-outline"}`}
          onClick={() => setActiveTab("company")}
        >
          <Building size={16} />
          Company Info
        </button>
        <button
          className={`btn btn-sm ${activeTab === "boxes" ? "btn-primary" : "btn-outline"}`}
          onClick={() => {
            setActiveTab("boxes");
            loadBoxSizes();
            loadPaperStocks();
          }}
        >
          <Box size={16} />
          Packaging ({boxSizes.length + paperStocks.length})
        </button>
        <button
          className={`btn btn-sm ${activeTab === "postoffices" ? "btn-primary" : "btn-outline"}`}
          onClick={() => setActiveTab("postoffices")}
        >
          <FileText size={16} />
          Post Offices ({postOffices.length})
        </button>
        <button
          className={`btn btn-sm ${activeTab === "addresses" ? "btn-primary" : "btn-outline"}`}
          onClick={() => setActiveTab("addresses")}
        >
          <MapPin size={16} />
          Saved Addresses ({settings.postage_statement_addresses?.length || 0})
        </button>
      </div>

      {/* Company Info Tab */}
      {activeTab === "company" && (
        <>
          <div className="form-section">
            <div className="form-title">Company Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.company_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-input"
                  value={settings.phone_number}
                  onChange={(e) => setSettings(prev => ({ ...prev, phone_number: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={settings.email}
                  onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-title">Company Address</div>
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.street_address}
                  onChange={(e) => setSettings(prev => ({ ...prev, street_address: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.city}
                  onChange={(e) => setSettings(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.state}
                  onChange={(e) => setSettings(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.zip}
                  onChange={(e) => setSettings(prev => ({ ...prev, zip: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-title">USPS Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Permit Number *</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.permit_number}
                  onChange={(e) => setSettings(prev => ({ ...prev, permit_number: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Permit Type</label>
                <select
                  className="form-select"
                  value={settings.permit_type}
                  onChange={(e) => setSettings(prev => ({ ...prev, permit_type: e.target.value }))}
                >
                  <option value="Permit Imprint">Permit Imprint</option>
                  <option value="Metered Mail">Metered Mail</option>
                  <option value="Precanceled Stamp">Precanceled Stamp</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">CRID Number</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.crid_number}
                  onChange={(e) => setSettings(prev => ({ ...prev, crid_number: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Mailer ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={settings.mailer_id}
                  onChange={(e) => setSettings(prev => ({ ...prev, mailer_id: e.target.value }))}
                />
              </div>
              {/*
                * Was a literal 45 in the shipment export, with this setting
                * sitting unread beside it and no way to change it short of SQL.
                */}
              <div className="form-group">
                <label className="form-label" htmlFor="handling-fee-percent">
                  Default Handling Fee (%)
                </label>
                <input
                  id="handling-fee-percent"
                  type="number"
                  min="0"
                  max="999.99"
                  step="0.01"
                  inputMode="decimal"
                  className="form-input"
                  value={settings.handling_fee_percent ?? ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, handling_fee_percent: e.target.value }))}
                />
                <p className="field-hint">
                  Written into every shipment CSV export, under Default Handling Fee
                  Percent.
                </p>
              </div>
              {/*
                * A flat fee on top of the percentage, not another way of saying
                * it. The billing format used to carry a Type column that marked
                * the amount as a percentage; it no longer does, so the two are
                * genuinely separate numbers and are added together downstream.
                */}
              <div className="form-group">
                <label className="form-label" htmlFor="handling-fee-amount">
                  Default Handling Fee ($)
                </label>
                <input
                  id="handling-fee-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className="form-input"
                  value={settings.handling_fee_amount ?? ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, handling_fee_amount: e.target.value }))}
                />
                <p className="field-hint">
                  A flat amount added per shipment, after the percentage. Written
                  into every shipment CSV export, under Default Handling Fee Amount.
                </p>
              </div>
            </div>
          </div>

          <UsersSection />

          <EmailSection />

          <SecretsSection />

          <AboutSection />

          {/* New PDF Templates Section */}
          <div className="form-section">
            <div className="form-title">PDF Templates</div>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1.5rem" }}>
              Upload your custom PDF templates. Data will be overlayed onto these templates when generating documents.
            </p>

            {/* PVDS Template */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label className="form-label">PVDS Form Template (2 pages, 8.5" x 11")</label>
              {settings.pvds_template ? (
                <StoredFile
                  path={settings.pvds_template}
                  label="PVDS Form Template"
                  onRemove={() => handleDeleteTemplate('pvds')}
                />
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleUploadTemplate('pvds', e.target.files[0])}
                    style={{ display: "none" }}
                    id="pvds-template-upload"
                  />
                  <label
                    htmlFor="pvds-template-upload"
                    className="btn btn-outline"
                    style={{
                      cursor: uploadingTemplate === 'pvds' ? 'wait' : 'pointer',
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    {uploadingTemplate === 'pvds' ? 'Uploading...' : 'Upload PVDS Template'}
                  </label>
                </div>
              )}
            </div>

            {/* Facing Slips Template */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label className="form-label">Facing Slips Template (8.5" x 11")</label>
              {settings.facing_slip_template ? (
                <StoredFile
                  path={settings.facing_slip_template}
                  label="Facing Slip Template"
                  onRemove={() => handleDeleteTemplate('facing_slip')}
                />
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleUploadTemplate('facing_slip', e.target.files[0])}
                    style={{ display: "none" }}
                    id="facing-slip-template-upload"
                  />
                  <label
                    htmlFor="facing-slip-template-upload"
                    className="btn btn-outline"
                    style={{
                      cursor: uploadingTemplate === 'facing_slip' ? 'wait' : 'pointer',
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    {uploadingTemplate === 'facing_slip' ? 'Uploading...' : 'Upload Facing Slip Template'}
                  </label>
                </div>
              )}
            </div>

            {/* Labels Template */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label className="form-label">Labels Template (Avery 5160 format)</label>
              {settings.label_template ? (
                <StoredFile
                  path={settings.label_template}
                  label="Label Template"
                  onRemove={() => handleDeleteTemplate('label')}
                />
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleUploadTemplate('label', e.target.files[0])}
                    style={{ display: "none" }}
                    id="label-template-upload"
                  />
                  <label
                    htmlFor="label-template-upload"
                    className="btn btn-outline"
                    style={{
                      cursor: uploadingTemplate === 'label' ? 'wait' : 'pointer',
                      width: "100%",
                      justifyContent: "center"
                    }}
                  >
                    {uploadingTemplate === 'label' ? 'Uploading...' : 'Upload Label Template'}
                  </label>
                </div>
              )}
            </div>

            {/* NEW: Signature PNG Upload */}
            <div>
              <label className="form-label">Signature for Postage Statement (PNG format)</label>
              <p style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "0.75rem" }}>
                Upload a signature image to overlay on the last page of the Postage Statement at X=5", Y=5.375"
              </p>
              {settings.signature_png ? (
                <StoredFile
                  path={settings.signature_png}
                  label="Signature"
                  contentType="image/png"
                  onRemove={handleDeleteSignature}
                />
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    type="file"
                    accept="image/png"
                    onChange={(e) => handleUploadSignature(e.target.files[0])}
                    style={{ display: "none" }}
                    id="signature-upload"
                  />
                  <label
                    htmlFor="signature-upload"
                    className="btn btn-outline"
                    style={{
                      cursor: uploadingTemplate === 'signature' ? 'wait' : 'pointer',
                      width: "100%",
                      justifyContent: "center",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem"
                    }}
                  >
                    <Image size={16} />
                    {uploadingTemplate === 'signature' ? 'Uploading...' : 'Upload Signature PNG'}
                  </label>
                </div>
              )}
            </div>
          </div>
          {/* End New PDF Templates Section */}


          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2rem" }}>
            <button
              className="btn btn-success"
              onClick={handleSaveSettings}
              disabled={saving}
            >
              <Save size={20} />
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </>
      )}

      {/* Box Sizes Tab */}
      {activeTab === "boxes" && (
        <>
          {/* Packaging - Boxes */}
          <div className="form-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div className="form-title" style={{ marginBottom: 0 }}>Boxes & Trays</div>
              <button
                className="btn btn-primary"
                onClick={() => {
                   setEditingBox(null); // Clear editing state for new item
                   setBoxForm({ name: "", type: "Box", width: 0, length: 0, height: 0, box_empty_weight: 0, wall_thickness_in: "0.25", active: true });
                  setShowBoxModal(true);
                  setError(""); // Clear previous errors
                }}
              >
                <Package size={20} />
                Add Box/Tray
              </button>
            </div>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
              Manage box and tray sizes for packing your mailings. These are used for packing and shipping calculations.
            </p>

            {boxSizes.length === 0 ? (
              <div style={{
                textAlign: "center",
                padding: "3rem",
                background: "#f8fafc",
                borderRadius: "8px",
                border: "2px dashed #cbd5e1"
              }}>
                <Box size={48} style={{ color: "#94a3b8", margin: "0 auto 1rem" }} />
                <p style={{ color: "#64748b", marginBottom: "0.5rem" }}>
                  No boxes or trays configured yet.
                </p>
                <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1rem" }}>
                  Click "Add Box/Tray" to get started.
                </p>
                <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                  <button className="btn btn-sm btn-outline" onClick={loadBoxSizes}>
                    Refresh Data
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={() => {
                    setEditingBox(null);
                    setBoxForm({
                      name: "",
                      type: "Box",
                      width: "",
                      length: "",
                      height: "",
                      box_empty_weight: "",
                      wall_thickness_in: "0.25",
                      active: true
                    });
                    setShowBoxModal(true);
                    setError("");
                  }}>
                    <Box size={16} />
                    Add Box/Tray
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "1rem" }}>
                {boxSizes.map((box) => (
                  <div
                    key={box.id}
                    style={{
                      background: box.active !== false ? "white" : "#f1f5f9",
                      border: `2px solid ${box.active !== false ? "#e2e8f0" : "#cbd5e1"}`,
                      padding: "1rem",
                      borderRadius: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      opacity: box.active !== false ? 1 : 0.6
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "1.125rem", fontWeight: 600, color: box.active !== false ? "#1e293b" : "#64748b" }}>
                        {box.name}
                        {box.active === false && (
                          <span style={{
                            marginLeft: "0.5rem",
                            fontSize: "0.75rem",
                            padding: "0.25rem 0.5rem",
                            background: "#fecaca",
                            color: "#991b1b",
                            borderRadius: "4px",
                            fontWeight: 500
                          }}>
                            INACTIVE
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b", marginTop: "0.25rem" }}>
                        Type: {box.type} • Inside: {box.length}&quot; × {box.width}&quot; × {box.height}&quot; • Empty Weight: {box.box_empty_weight} lbs
                      </div>
                      {/*
                        * Both sets of numbers, because they are used for
                        * different things and only one of them is billable.
                        * Showing the shipping size here also means a wall
                        * thickness that got zeroed is visible at a glance
                        * rather than only on the invoice.
                        */}
                      <div style={{ fontSize: "0.8125rem", color: "#475569", marginTop: "0.125rem" }}>
                        Ships as{' '}
                        <strong>
                          {(Number(box.length) + 2 * Number(box.wall_thickness_in ?? 0)).toFixed(2)}&quot; ×{' '}
                          {(Number(box.width) + 2 * Number(box.wall_thickness_in ?? 0)).toFixed(2)}&quot; ×{' '}
                          {(Number(box.height) + 2 * Number(box.wall_thickness_in ?? 0)).toFixed(2)}&quot;
                        </strong>{' '}
                        <span className="muted">
                          ({Number(box.wall_thickness_in ?? 0)}&quot; wall
                          {Number(box.wall_thickness_in ?? 0) === 0 && box.type === 'Box'
                            ? ' — set this, or the carrier is quoted the inside'
                            : ''}
                          )
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={async () => {
                          try {
                            await api.entities.BoxSize.update(box.id, {
                              active: box.active === false ? true : false
                            });
                            setSuccess(box.active === false ? "Box/Tray activated!" : "Box/Tray deactivated!");
                            setTimeout(() => setSuccess(""), 2000);
                            loadBoxSizes();
                          } catch (err) {
                            console.error(err);
                            setError("Failed to update box/tray status");
                            setTimeout(() => setError(""), 3000);
                          }
                        }}
                        style={{
                          color: box.active !== false ? "#f59e0b" : "#10b981",
                          borderColor: box.active !== false ? "#f59e0b" : "#10b981"
                        }}
                      >
                        {box.active !== false ? "Deactivate" : "Activate"}
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setEditingBox(box);
                          setBoxForm({
                            name: box.name,
                            type: box.type,
                            width: box.width,
                            length: box.length,
                            height: box.height,
                            box_empty_weight: box.box_empty_weight,
                            // Without this the field opens empty and saves as
                            // zero, so editing a box for any reason silently
                            // wiped its wall thickness and the shipping size
                            // collapsed back to the interior.
                            wall_thickness_in: box.wall_thickness_in ?? 0,
                            active: box.active !== false
                          });
                          setShowBoxModal(true);
                          setError(""); // Clear previous errors
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={async () => {
                          if (confirm(`Delete ${box.name}?`)) {
                            try {
                              await api.entities.BoxSize.delete(box.id);
                              setSuccess("Box/Tray deleted!");
                              setTimeout(() => setSuccess(""), 2000);
                              loadBoxSizes();
                            } catch (err) {
                              console.error(err);
                              setError("Failed to delete box/tray");
                              setTimeout(() => setError(""), 3000);
                            }
                          }
                        }}
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Paper Stock Section */}
          <div className="form-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div className="form-title" style={{ marginBottom: 0 }}>Paper Stock</div>
              <button className="btn btn-sm btn-primary" onClick={handleAddPaperStock}>
                <Package size={16} />
                Add Paper Stock
              </button>
            </div>
            <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
              Define paper stock specifications to calculate how many pieces/bundles can fit in a box.
            </p>

            {paperStocks.length === 0 ? (
              <div style={{
                textAlign: "center",
                padding: "3rem",
                background: "#f8fafc",
                borderRadius: "8px",
                border: "2px dashed #cbd5e1"
              }}>
                <Package size={48} style={{ color: "#94a3b8", margin: "0 auto 1rem" }} />
                <p style={{ color: "#64748b", marginBottom: "0.5rem" }}>
                  No paper stocks defined
                </p>
                <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1rem" }}>
                  Add paper stock specifications to help calculate box capacity
                </p>
                <button className="btn btn-sm btn-primary" onClick={handleAddPaperStock}>
                  <Package size={16} />
                  Add Paper Stock
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {paperStocks.map((paper) => (
                  <div
                    key={paper.id}
                    className="card"
                    style={{
                      padding: "1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.2s ease",
                      border: "2px solid #e2e8f0"
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "1.125rem", color: "#1e293b", marginBottom: "0.25rem" }}>
                        {paper.paper_name}
                      </div>
                      <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                        Size: {paper.width}" × {paper.length}" • Thickness: {paper.thickness}" • Weight: {paper.weight_lbs_per_100} lbs
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handleEditPaper(paper)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handleDeletePaperStock(paper.id)}
                        style={{ color: "#dc2626", borderColor: "#dc2626" }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Post Offices Tab */}
      {activeTab === "postoffices" && (
        <div className="form-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div className="form-title" style={{ marginBottom: 0 }}>Custom Post Offices</div>
            <button className="btn btn-sm btn-primary" onClick={handleAddPostOffice}>
              <FileText size={16} />
              Add Post Office
            </button>
          </div>
          <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
            Add custom post office addresses for destinations that aren't automatically detected.
          </p>
          {postOffices.length === 0 ? (
            <p style={{ textAlign: "center", color: "#64748b", padding: "2rem" }}>
              No custom post offices defined. Click "Add Post Office" to create one.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {postOffices.map((po) => (
                <div key={po.id} className="card" style={{ padding: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>{po.name}</div>
                    <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                      {po.address_line1}
                      {po.address_line2 && `, ${po.address_line2}`}
                      {po.city && `, ${po.city}`}
                      {po.state && `, ${po.state}`}
                      {po.zip && ` ${po.zip}`}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handleDeletePostOffice(po.id)}
                    style={{ color: "#dc2626" }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Saved Addresses Tab */}
      {activeTab === "addresses" && (
        <div className="form-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <div className="form-title" style={{ marginBottom: 0 }}>Postage Statement Addresses</div>
          </div>
          <p style={{ color: "#64748b", fontSize: "0.875rem", marginBottom: "1rem" }}>
            Saved addresses from previous Postage Statement uploads. These are available for quick selection when uploading new forms.
          </p>

          {!settings.postage_statement_addresses || settings.postage_statement_addresses.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "3rem",
              background: "#f8fafc",
              borderRadius: "8px",
              border: "2px dashed #cbd5e1"
            }}>
              <MapPin size={48} style={{ color: "#94a3b8", margin: "0 auto 1rem" }} />
              <p style={{ color: "#64748b", marginBottom: "0.5rem" }}>
                No saved addresses yet
              </p>
              <p style={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                Addresses will be saved automatically when you upload Postage Statement PDFs
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {settings.postage_statement_addresses.map((addr, index) => (
                <div
                  key={index}
                  className="card"
                  style={{
                    padding: "1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "all 0.2s ease",
                    border: "2px solid #e2e8f0"
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: "1.125rem", color: "#1e293b", marginBottom: "0.25rem" }}>
                      {addr.name}
                    </div>
                    <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                      {addr.address}<br />
                      {addr.city}, {addr.state} {addr.zip}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline"
                    onClick={() => handleDeleteSavedAddress(index)}
                    style={{ color: "#dc2626", borderColor: "#dc2626" }}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Box Modal */}
      {showBoxModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "1rem"
        }}>
          <div className="card" style={{ maxWidth: "500px", width: "100%", boxSizing: "border-box", overflowY: "auto", maxHeight: "90vh" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>
              {editingBox ? "Edit Box/Tray" : "Add Box/Tray"}
            </h3>

            <div className="form-group" style={{ marginBottom: "1rem" }}>
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-input"
                value={boxForm.name}
                onChange={(e) => setBoxForm({ ...boxForm, name: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: "1rem" }}>
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={boxForm.type}
                onChange={(e) => setBoxForm({ ...boxForm, type: e.target.value })}
              >
                <option value="Box">Box</option>
                <option value="Tray">Tray</option>
              </select>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem", width: "100%", boxSizing: "border-box" }}>
              <div className="form-group" style={{ minWidth: 0 }}>
                <label className="form-label">Length (in)</label>
                <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={boxForm.length}
                    onChange={(e) => setBoxForm({ ...boxForm, length: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ minWidth: 0 }}>
                <label className="form-label">Width (in)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={boxForm.width}
                  onChange={(e) => setBoxForm({ ...boxForm, width: e.target.value })}
                />
              </div>
              <div className="form-group" style={{ minWidth: 0 }}>
                <label className="form-label">Height (in)</label>
                <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={boxForm.height}
                    onChange={(e) => setBoxForm({ ...boxForm, height: e.target.value })}
                />
              </div>
            </div>

            <div className="user-grid" style={{ marginBottom: "1rem" }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Empty Weight (lbs)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={boxForm.box_empty_weight}
                  onChange={(e) => setBoxForm({ ...boxForm, box_empty_weight: e.target.value })}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Wall Thickness (in)</label>
                <input
                  type="number"
                  step="0.005"
                  min="0"
                  className="form-input"
                  value={boxForm.wall_thickness_in}
                  onChange={(e) => setBoxForm({ ...boxForm, wall_thickness_in: e.target.value })}
                />
                <p className="field-hint">
                  Board thickness of one wall. The dimensions above are the
                  inside, which is what decides what fits; a carrier charges for
                  the outside, so it is quoted as inside + twice this.
                  {boxForm.width && boxForm.length && boxForm.height ? (
                    <>
                      {" "}Ships as{" "}
                      <strong>
                        {(parseFloat(boxForm.length || 0) + 2 * (parseFloat(boxForm.wall_thickness_in) || 0)).toFixed(2)}
                        {" × "}
                        {(parseFloat(boxForm.width || 0) + 2 * (parseFloat(boxForm.wall_thickness_in) || 0)).toFixed(2)}
                        {" × "}
                        {(parseFloat(boxForm.height || 0) + 2 * (parseFloat(boxForm.wall_thickness_in) || 0)).toFixed(2)}
                        {"″"}
                      </strong>
                      .
                    </>
                  ) : null}
                </p>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={boxForm.active}
                  onChange={(e) => setBoxForm({ ...boxForm, active: e.target.checked })}
                  style={{ width: "18px", height: "18px" }}
                />
                <span className="form-label" style={{ margin: 0 }}>Active (available for use)</span>
              </label>
            </div>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowBoxModal(false);
                  setEditingBox(null);
                  setError(""); // Clear previous errors
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveBox}
                disabled={savingBox}
              >
                {savingBox ? "Saving..." : (editingBox ? "Update" : "Add")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paper Stock Modal */}
      {editingPaper && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "1rem",
          overflow: "auto"
        }}>
          <div className="card" style={{ maxWidth: "600px", width: "100%", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "1rem" }}>
              {editingPaper === "new" ? "Add Paper Stock" : "Edit Paper Stock"}
            </h3>

            <div className="form-group" style={{ marginBottom: "1rem" }}>
              <label className="form-label">Paper Name *</label>
              <input
                type="text"
                className="form-input"
                value={paperForm.paper_name}
                onChange={(e) => setPaperForm({ ...paperForm, paper_name: e.target.value })}
                placeholder="e.g., Standard Glossy, Cardstock"
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: "0.8rem" }}>Width (in) *</label>
                <input
                   type="number"
                   step="0.001"
                   className="form-input"
                   value={paperForm.width}
                   onChange={(e) => setPaperForm({ ...paperForm, width: e.target.value })}
                  style={{ padding: "0.5rem" }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: "0.8rem" }}>Length (in) *</label>
                <input
                   type="number"
                   step="0.001"
                   className="form-input"
                   value={paperForm.length}
                   onChange={(e) => setPaperForm({ ...paperForm, length: e.target.value })}
                  style={{ padding: "0.5rem" }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: "0.8rem" }}>Thickness (in) *</label>
                <input
                  type="number"
                  step="0.0001"
                  className="form-input"
                  value={paperForm.thickness}
                  onChange={(e) => setPaperForm({ ...paperForm, thickness: e.target.value })}
                  style={{ padding: "0.5rem" }}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: "0.8rem" }}>Weight per 100 pieces (lbs) *</label>
                <input
                  type="number"
                  step="0.001"
                  className="form-input"
                  value={paperForm.weight_lbs_per_100}
                  onChange={(e) => setPaperForm({ ...paperForm, weight_lbs_per_100: e.target.value })}
                  style={{ padding: "0.5rem" }}
                />
              </div>
            </div>

            <p style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "1.5rem" }}>
              Paper dimensions and weight are used to calculate how many pieces can fit in a box
            </p>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                className="btn btn-outline"
                onClick={() => {
                  setEditingPaper(null);
                  setError("");
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSavePaper}
                disabled={savingPaper}
              >
                {savingPaper ? "Saving..." : (editingPaper === "new" ? "Add" : "Save Changes")}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      );
      }